import { z } from 'zod'
import { undefinedWhenEmpty } from '@/utils/form.ts'
import { isValidPhoneNumber } from 'libphonenumber-js'
import type { UserEmployee } from '@/features/user/api/user-employee.data.ts'
import { formatISO } from 'date-fns'

export const UserRole = z.enum(
  [
    'ADMIN',
    'CUSTOMER_ADMIN',
    'FLEET_ADVISOR',
    'MARKET_MANAGER',
    'FINANCIAL_MANAGER',
    'FLEET_MANAGER',
    'DISPATCHER',
    'DRIVER'
  ],
  '@feature_user:api.message.errors.userValidationFailed.role.emptyMessage'
)

export const RoleArraySchema = z.object({
  roles: z.array(UserRole)
})

export type RoleList = z.infer<typeof RoleArraySchema>
export type UserRoleType = z.infer<typeof UserRole>

export const UserLanguageSchema = z.enum(['en', 'fr'], {
  message: '@feature_user:api.message.errors.userValidationFailed.language.invalidMessage'
})

const CompanyWideRestrictionSchema = z.object({
  type: z.literal('COMPANY')
})

const SiteRestrictionSchema = z.object({
  type: z.literal('RESTRICTED'),
  sites: z
    .array(
      z.string(),
      '@feature_user:api.message.errors.userValidationFailed.site.emptyMessage'
    )
    .min(1, '@feature_user:api.message.errors.userValidationFailed.site.emptyMessage')
})

export const UserRestrictionsSchema = z.discriminatedUnion('type', [
  CompanyWideRestrictionSchema,
  SiteRestrictionSchema
])

const PhoneNumberSchema = z
  .string(
    '@feature_user:api.message.errors.userValidationFailed.phoneNumber.emptyMessage'
  )
  .min(
    10,
    '@feature_user:api.message.errors.userValidationFailed.phoneNumber.invalidInputSize'
  )
  .refine(
    val => isValidPhoneNumber(val),
    '@feature_user:api.message.errors.userValidationFailed.phoneNumber.invalidFormat'
  )
  .transform(val => val.replaceAll(/[\s-]/g, ''))

const BaseFormSchema = z.object({
  // User Role
  role: UserRole,

  password: z.string().optional(),

  // Personal Information
  firstName: z
    .string(
      '@feature_user:api.message.errors.userValidationFailed.firstName.emptyMessage'
    )
    .min(
      1,
      '@feature_user:api.message.errors.userValidationFailed.firstName.emptyMessage'
    ),
  lastName: z
    .string('@feature_user:api.message.errors.userValidationFailed.lastName.emptyMessage')
    .min(
      1,
      '@feature_user:api.message.errors.userValidationFailed.lastName.emptyMessage'
    ),

  language: UserLanguageSchema,

  // Contact Information
  phoneNumber: PhoneNumberSchema,
  email: z
    .email('@feature_user:api.message.errors.userValidationFailed.email.invalidMessage')
    .trim(),

  // Site assignments
  siteRestrictions: UserRestrictionsSchema
})

export const DriverFormSchema = BaseFormSchema.extend({
  role: UserRole.extract(['DRIVER']),
  phoneNumber: BaseFormSchema.shape.phoneNumber,
  email: undefinedWhenEmpty(BaseFormSchema.shape.email).optional(),
  contract: z
    .object({
      startDate: z.date().nullish(),
      endDate: z.date().nullish()
    })
    .refine(contract => !contract.endDate || contract.startDate, {
      error: '@feature_user:components.UserDetailsForm.form.contractStartDate.missingDateError',
      path: ['startDate']
    })
    .refine(
      contract =>
        !contract.startDate || !contract.endDate || contract.endDate > contract.startDate,
      {
        error: '@feature_user:components.UserDetailsForm.form.contractEndDate.invalidMessage',
        path: ['endDate']
      }
    )
})

export const OtherUserFormSchema = BaseFormSchema.extend({
  role: UserRole.exclude(['DRIVER']),
  email: BaseFormSchema.shape.email.nonempty(
    '@feature_user:api.message.errors.userValidationFailed.email.emptyMessage'
  ),
  phoneNumber: undefinedWhenEmpty(BaseFormSchema.shape.phoneNumber).optional()
})

export const CreateUserFormSchema = z.discriminatedUnion(
  'role',
  [DriverFormSchema, OtherUserFormSchema],
  { error: '@feature_user:api.message.errors.userValidationFailed.role.emptyMessage' }
)

export const EditUserFormSchema = (originalUser: UserEmployee) =>
  z.discriminatedUnion('role', [
    DriverFormSchema.refine(() => originalUser.role === 'DRIVER', {
      message: 'Cannot change role to a driver',
      path: ['role']
    }).refine(data => data.phoneNumber === originalUser.phoneNumber, {
      message: 'Cannot change phone number for drivers',
      path: ['phoneNumber']
    }),

    OtherUserFormSchema.refine(() => originalUser.role !== 'DRIVER', {
      message: 'Cannot change role of a driver',
      path: ['role']
    }).refine(data => data.email === originalUser.email, {
      message: 'Cannot change email for non-drivers users',
      path: ['email']
    })
  ])

export default CreateUserFormSchema

export const UserRequestSchema = z.discriminatedUnion('role', [
  DriverFormSchema.omit({
    password: true,
    contract: true,
    siteRestrictions: true
  }).safeExtend({
    tenantId: z.string().optional(),
    siteRestrictions: z.array(z.string()).min(1).nullable(),
    contract: z
      .object({
        startDate: z.iso.date().optional(),
        endDate: z.iso.date().optional()
      })
      .optional(),
    login: z.object({
      type: z.literal('PHONE_NUMBER'),
      identifier: z.string().min(1, 'Identifier is required')
    })
  }),
  OtherUserFormSchema.omit({ password: true, siteRestrictions: true }).safeExtend({
    tenantId: z.string().optional(),
    siteRestrictions: z.array(z.string()).min(1).nullable(),
    login: z.object({
      type: z.literal('EMAIL'),
      identifier: z.string().min(1, 'Identifier is required')
    })
  })
])

// Response schema for a created user
export const UserSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.email().nullable(),
  phoneNumber: z.string().nullable(),
  isActivated: z.boolean(),
  role: UserRole,
  login: z.object({ type: z.string(), identifier: z.string() }),
  language: UserLanguageSchema
})

export const UserInfosSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.email().nullable(),
  phoneNumber: z.string().nullable(),
  role: z.string(),
  permissions: z.array(z.string()),
  language: UserLanguageSchema.default('en')
})

export const UserSettingsFormSchema = z.object({
  language: UserLanguageSchema
})

export type CreateUserForm = z.infer<typeof CreateUserFormSchema>
export type UserSettingsForm = z.infer<typeof UserSettingsFormSchema>
export type UserRequest = z.infer<typeof UserRequestSchema>
export type User = z.infer<typeof UserSchema>
export type UserInfo = z.infer<typeof UserInfosSchema>

const ROLE_ORDER: UserRoleType[] = [
  'DRIVER',
  'DISPATCHER',
  'FLEET_MANAGER',
  'FLEET_ADVISOR',
  'FINANCIAL_MANAGER',
  'MARKET_MANAGER',
  'CUSTOMER_ADMIN',
  'ADMIN'
] as const

export function sortRoles(roles: UserRoleType[]): UserRoleType[] {
  return roles.sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b))
}

export function toDriverRequest(
  tenantId: string | null,
  formData: z.infer<typeof DriverFormSchema>
): UserRequest {
  return {
    ...formData,
    tenantId: tenantId || undefined,
    siteRestrictions:
      formData.siteRestrictions.type === 'RESTRICTED'
        ? formData.siteRestrictions.sites
        : null,
    contract: formData.contract?.startDate
      ? {
          startDate: formData.contract?.startDate
            ? formatISO(formData.contract?.startDate, {
                representation: 'date'
              })
            : undefined,
          endDate: formData.contract?.endDate
            ? formatISO(formData.contract?.endDate, { representation: 'date' })
            : undefined
        }
      : undefined,
    login: { type: 'PHONE_NUMBER', identifier: formData.phoneNumber }
  }
}

export function toOtherUserRequest(
  tenantId: string | null,
  formData: z.infer<typeof OtherUserFormSchema>
): UserRequest {
  return {
    ...formData,
    tenantId: tenantId || undefined,
    siteRestrictions:
      formData.siteRestrictions.type === 'RESTRICTED'
        ? formData.siteRestrictions.sites
        : null,
    login: { type: 'EMAIL', identifier: formData.email }
  }
}
